<script setup lang="ts">
import {PeriodComparisonWidget} from "@/laboratory/components/dashboard/dashboard";
import {Widget} from "@/laboratory/components/dashboard/components/widget";
import {computed} from "vue";
import {ArrowDown, ArrowUp, Equal} from "@lucide/vue";

const props = defineProps<{
    widget: PeriodComparisonWidget;
}>();

const decimals = computed(() => props.widget.data.decimals ?? 0);

const format = (value: number, signed = false) => new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimals.value,
    maximumFractionDigits: decimals.value,
    signDisplay: signed ? "exceptZero" : "auto"
}).format(value);

// Rounded to the displayed precision so that "2,3 vs 2,3" never shows a non-zero evolution.
const delta = computed(() => {
    const factor = 10 ** decimals.value;

    return Math.round((props.widget.data.current.value - props.widget.data.previous.value) * factor) / factor;
});

const trend = computed(() => {
    if (delta.value > 0) {
        return {icon: ArrowUp, color: "text-green"};
    }

    if (delta.value < 0) {
        return {icon: ArrowDown, color: "text-red"};
    }

    return {icon: Equal, color: "text-muted-foreground"};
});
</script>

<template>
  <Widget :widget="widget">
    <div class="space-y-6">
      <div class="grid grid-cols-3 divide-x divide-gray py-2 text-center">
        <div class="flex flex-col items-center gap-1">
          <span class="text-sm font-semibold text-primary">{{ widget.data.current.label }}</span>
          <span class="text-4xl font-bold text-primary">{{ format(widget.data.current.value) }}</span>
        </div>
        <div class="flex flex-col items-center gap-1">
          <span class="text-sm text-muted-foreground">{{ widget.data.previous.label }}</span>
          <span class="text-4xl font-bold text-primary/60">{{ format(widget.data.previous.value) }}</span>
        </div>
        <div class="flex flex-col items-center gap-1" :class="trend.color">
          <span class="text-sm font-semibold">{{ format(delta, true) }}</span>
          <component :is="trend.icon" class="w-10 h-10" />
        </div>
      </div>
      <p v-if="widget.data.reference" class="text-sm">
        {{ widget.data.reference.label }} :
        <span class="font-bold text-primary">{{ format(widget.data.reference.value) }}</span>
      </p>
    </div>
  </Widget>
</template>
